import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, KpiCard, StatusPill, Button, CRITICALITY, AVAILABILITY } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';
import { AssetForm, type AssetRow } from '../components/EntityForms';

type Row = AssetRow & {
  qr_token: string;
  properties: { name: string } | null; asset_categories: { name: string } | null;
};

export function AssetsList() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AssetRow | null>(null);

  const load = useCallback(async () => {
    const r = await supabase
      .from('assets')
      .select('id,name,qr_token,criticality,availability,property_id,zone_id,category_id,brand,model,serial_number,in_service_date,properties(name),asset_categories(name)')
      .is('deleted_at', null)
      .order('name');
    setRows((r.data as unknown as Row[]) ?? []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (r: Row) => { setEditing(r); setFormOpen(true); };

  const kpis = useMemo(() => {
    const critical = rows.filter((r) => r.criticality === 'critical' || r.criticality === 'high').length;
    const down = rows.filter((r) => r.availability !== 'available').length;
    return { total: rows.length, critical, down };
  }, [rows]);

  return (
    <Layout
      header={
        <Topbar
          actions={
            <>
              <Button variant="secondary" onClick={() => nav('/assets/import')}>{t('csv.title')}</Button>
              <Button variant="secondary" onClick={() => nav('/assets/labels')}>{t('qr.labels')}</Button>
              <Button onClick={openNew}>+ {t('form.newAsset')}</Button>
            </>
          }
        />
      }
    >
      <div className="flex flex-col gap-[18px] px-7 pt-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.assets')}</h1>
        <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
          <KpiCard label={t('nav.assets')} value={kpis.total} dark />
          <KpiCard label={t('criticality.critical')} value={<span className="text-st-overdue-fg">{kpis.critical}</span>} />
          <KpiCard label={t('availability.unavailable')} value={<span className="text-st-onhold-fg">{kpis.down}</span>} />
        </section>
      </div>
      <div className="px-7 pb-7 pt-[18px]">
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="px-5 py-3 font-medium">{t('col.qr')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.name')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.category')}</th>
                  <th className="px-2 py-3 font-medium">{t('filters.property')}</th>
                  <th className="px-2 py-3 font-medium">{t('col.criticality')}</th>
                  <th className="px-2 py-3 font-medium">{t('asset.statusCol')}</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-5 py-6 text-muted">{t('common.loading')}</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-6 text-muted">{t('common.noData')}</td></tr>
                ) : (
                  rows.map((r) => {
                    const crit = CRITICALITY[r.criticality];
                    const avail = AVAILABILITY[r.availability];
                    return (
                      <tr key={r.id} className="cursor-pointer border-t border-divider hover:bg-bg" onClick={() => nav(`/assets/${r.id}`)}>
                        <td className="px-5 py-3.5 font-mono text-xs text-muted">{r.qr_token}</td>
                        <td className="px-2 py-3.5 font-medium">{r.name}</td>
                        <td className="px-2 py-3.5 text-muted">{r.asset_categories?.name ?? '—'}</td>
                        <td className="px-2 py-3.5 text-muted">{r.properties?.name ?? '—'}</td>
                        <td className="px-2 py-3.5">{crit && <StatusPill tone={crit.tone}>{t(crit.labelKey)}</StatusPill>}</td>
                        <td className="px-2 py-3.5">{avail && <StatusPill tone={avail.tone}>{t(avail.labelKey)}</StatusPill>}</td>
                        <td className="px-5 py-3.5 text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEdit(r); }}>{t('form.edit')}</Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <AssetForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={load} initial={editing} />
    </Layout>
  );
}
